import React from "react";
import {
  Box,
  Flex,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  InputGroup,
  Link,
  InputLeftElement,
  InputRightElement
} from "@chakra-ui/react";
import NextLink from "next/link";
import { css } from "@emotion/react";
import { EmailIcon, LockIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import Image from "next/image";
import QoreBanner from "@modules/auth/QoreBanner";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import { useRouter } from "next/router";
import { sessionTokenState } from "app/common/SessionProvider";
import { useSetRecoilState } from "recoil";
import qoreContext from "app/common/qoreContext";
import {
  userExternalIdState,
  userIdState
} from "@modules/dashboard/dashboard-store";

const userLoginSchema = Yup.object({
  email: Yup.string().email().required(),
  password: Yup.string().required()
}).required();

const hoverTextLink = css`
  &:hover {
    color: #3181ce;
    cursor: pointer;
    width: min;
  }
`;
const LoginPage: React.FC = () => {
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(userLoginSchema)
  });
  const router = useRouter();
  const fields = watch();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorLogin, setErrorLogin] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const client = qoreContext.useClient();
  const setSessionToken = useSetRecoilState(sessionTokenState);
  const setUserId = useSetRecoilState(userIdState);
  const setUserExternalId = useSetRecoilState(userExternalIdState);

  const onSubmit: SubmitHandler<{
    email: string;
    password: string;
  }> = async data => {
    try {
      setIsLoading(true);
      const response = await axios.post(`/api/authenticate`, {
        identifier: data.email,
        password: data.password
      });

      const token = response.data;

      if (token) {
        setSessionToken(token);
        const getUserData = await client.project.axios.get(`/v1/user`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setUserId(getUserData.data.id);
        setUserExternalId(getUserData.data.external_id);
        window.location.href = "/";
      }
    } catch (error) {
      if (error.response.data.message === "account-inactive") {
        router.push(`/auth/verify?email=${data.email}`);
      } else if (error.response.data.message === "need-reset") {
        router.push(`/auth/reset-password`);
      } else {
        setErrorLogin(true);
        setIsLoading(false);
      }
    }
  };

  return (
    <Flex width="100vw" height="100vh">
      <Box
        display="flex"
        flexDir="column"
        justifyContent="center"
        width="xl"
        alignItems="center"
      >
        <Box
          width="60%"
          h="80%"
          display="flex"
          flexDir="column"
          justifyContent="space-evenly"
          w={{ base: "83%", sm: "83%", md: "66%", lg: "58%" }}
        >
          <Box>
            <Image
              width="160%"
              height="100%"
              src="/assets/qore-logo.svg"
              alt="Qore"
              objectFit="contain"
            />
          </Box>

          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Get Started
            </Text>
            <Text fontSize="md">Hello there and welcome back!</Text>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
              <FormControl isRequired>
                <FormLabel htmlFor="email">Email</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <EmailIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    {...register("email")}
                    type="email"
                    placeholder="Enter email"
                    borderColor="gray.300"
                  />
                </InputGroup>
                <Text color="red.400">{errors.email?.message}</Text>
              </FormControl>

              <FormControl isRequired mt="5">
                <FormLabel htmlFor="password">Password</FormLabel>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <LockIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    {...register("password")}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    borderColor="gray.300"
                    isInvalid={errorLogin}
                    errorBorderColor="crimson"
                  />

                  <InputRightElement width="4.5rem">
                    <Button
                      ml="8"
                      size="sm"
                      onClick={() => setShowPassword(!showPassword)}
                      variant="unstyled"
                    >
                      {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                {errorLogin && (
                  <Text pt={2} color="red.400">
                    Oops! email or password your entered is incorrect.
                  </Text>
                )}
              </FormControl>

              <Flex w="full" justifyContent="end" mt="5">
                <NextLink href="/auth/forgot-password" passHref>
                  <Text color="#3181ce" cursor="pointer">
                    Forgot Password
                  </Text>
                </NextLink>
              </Flex>

              <Button
                w="full"
                mt="5"
                colorScheme="messenger"
                type="submit"
                disabled={!fields.email || !fields.password || isLoading}
                isLoading={isLoading}
                spinnerPlacement="start"
                loadingText="Signing In"
              >
                Sign In With Email
              </Button>

              <Flex
                w="full"
                justify="center"
                fontSize="14px"
                mt="5"
                textAlign="center"
                hidden
              >
                <Text>Not registered yet ? </Text>
                <NextLink href="/auth/register" passHref>
                  <Text ml="1" css={hoverTextLink}>
                    Create An Account
                  </Text>
                </NextLink>
              </Flex>
            </Box>
          </form>
        </Box>
      </Box>

      <QoreBanner />
    </Flex>
  );
};

export default LoginPage;
